import app from 'flarum/forum/app';

/**
 * Tag pages, Reddit-style:
 *   - Join (and, on phones and tablets, Start a discussion) under the tag's name, with its member count
 *   - Community highlights: the tag's pinned discussions as cards above the feed
 *   - About card: the Data Hub's key facts, the admin's text, useful links and ground rules. Right column
 *     on desktop; a collapsible block above the feed on phones and tablets.
 * All from GET /voucha-theme/tag-about/{slug} (one request per tag, shared by every piece).
 */

/** Per slug: undefined = not asked, null = loading, false = failed, object = loaded */
const cache = {};
export function tagAbout(slug) {
  if (!slug) return null;
  if (cache[slug] === undefined) {
    cache[slug] = null;
    app.request({
      method: 'GET',
      url: app.forum.attribute('apiUrl') + '/voucha-theme/tag-about/' + encodeURIComponent(slug),
      errorHandler: () => {}
    }).then(r => {
      cache[slug] = r && r.found ? r : false;
      m.redraw();
    }).catch(() => {
      cache[slug] = false;
    });
  }
  return cache[slug] || null;
}

/** Drop a tag's data (after joining, so the member count is fresh next time) */
export function forgetTagAbout(slug) {
  delete cache[slug];
}

/** Ids of the pinned discussions shown as highlights on this tag (the feed leaves them out) */
export function highlightIds(slug) {
  const d = slug ? cache[slug] : null;
  if (!d || !d.highlights || !d.highlights.length || highlightsClosed()) return [];
  return d.highlights.map(h => String(h.id));
}
function hasAboutCard(d) {
  return !!(d && (d.facts && d.facts.length || d.about || d.guide || d.links && d.links.length || d.rules && d.rules.length));
}
const short = n => n >= 1e6 ? +(n / 1e6).toFixed(1) + 'M' : n >= 1e3 ? +(n / 1e3).toFixed(1) + 'K' : String(n);

/** In-site links open without a page load; everything else in a new tab */
function linkAttrs(href) {
  if (/^\/(?!\/)/.test(href)) {
    return {
      href,
      onclick: e => {
        if (e.ctrlKey || e.metaKey || e.shiftKey) return;
        e.preventDefault();
        m.route.set(href);
      }
    };
  }
  return {
    href,
    target: '_blank',
    rel: 'noopener'
  };
}
function store(key, value) {
  try {
    if (value === undefined) return localStorage.getItem(key);
    if (value === null) localStorage.removeItem(key);else localStorage.setItem(key, value);
  } catch (e) {}
  return null;
}
function highlightsClosed() {
  return store('voucha-highlights-closed') === '1';
}

// ---------- Join ----------

function subscriptionLabel(sub) {
  const keys = {
    follow: 'following',
    lurk: 'lurking',
    ignore: 'ignoring',
    hide: 'hiding'
  };
  return keys[sub] ? app.translator.trans('fof-follow-tags.forum.sub_controls.' + keys[sub] + '_button') : app.translator.trans('fof-follow-tags.forum.sub_controls.follow_button');
}
function openSignUp() {
  try {
    const shown = app.modal.show(() => flarum.reg.asyncModuleImport('flarum/forum/components/SignUpModal'));
    if (shown && shown.catch) shown.catch(() => {});
  } catch (e) {}
}

/**
 * Join: one tap joins (no popup). Once joined it shows the level ("Joined", "All activity"…) and opens
 * Follow Tags' own window to change it or leave. Visitors are asked to sign up first.
 */
export class TagJoinButton {
  view(vnode) {
    const tag = vnode.attrs.model;
    const user = app.session.user;
    const sub = user && typeof tag.subscription === 'function' ? tag.subscription() : null;
    const joined = sub && sub !== 'not_follow';
    const icon = {
      follow: 'fas fa-check',
      lurk: 'fas fa-eye',
      ignore: 'fas fa-bell-slash',
      hide: 'fas fa-eye-slash'
    }[sub] || 'fas fa-plus';
    const click = () => {
      if (!user) return openSignUp();
      if (this.saving) return;
      if (!joined) {
        this.saving = true;
        tag.save({
          subscription: 'follow'
        }).then(() => {
          const d = cache[tag.slug()];
          if (d && d.members != null) d.members += 1;
        }).catch(() => {}).then(() => {
          this.saving = false;
          m.redraw();
        });
        return;
      }
      const shown = app.modal.show(() => flarum.reg.asyncModuleImport('ext:fof/follow-tags/forum/components/SubscriptionModal'), {
        model: tag
      });
      if (shown && shown.catch) shown.catch(() => {});
    };
    return m('button', {
      type: 'button',
      className: 'Button VouchaJoin' + (joined ? ' is-joined' : ' Button--primary'),
      disabled: this.saving,
      'aria-label': joined ? subscriptionLabel(sub) + ': change or leave ' + tag.name() : 'Join ' + tag.name(),
      onclick: click
    }, [m('i', {
      className: 'Button-icon ' + icon,
      'aria-hidden': 'true'
    }), m('span', joined ? subscriptionLabel(sub) : 'Join')]);
  }
}

/** Under the tag's name: Start a discussion (phones and tablets; desktop has it in the left column), Join, counts */
export function heroActions(tag) {
  const d = tagAbout(tag.slug());
  const counts = [];
  // shown once there are a few (a handful of visitors reads as an empty room)
  if (d && d.visitors != null && d.visitors >= 10) counts.push(short(d.visitors) + ' weekly visitors');
  if (d && d.members != null && d.members >= 10) counts.push(short(d.members) + ' members');
  if (d && d.week > 0) counts.push(d.week + (d.week === 1 ? ' new discussion' : ' new discussions') + ' this week');
  const start = () => {
    const btn = document.querySelector('.IndexPage-newDiscussion');
    if (btn) btn.click();
  };
  return m('div', {
    className: 'VouchaTagActions'
  }, [m('div', {
    className: 'VouchaTagActions-buttons'
  }, [m('button', {
    type: 'button',
    className: 'Button VouchaTagActions-start',
    onclick: start
  }, [m('i', {
    className: 'Button-icon fas fa-plus',
    'aria-hidden': 'true'
  }), m('span', 'Start a discussion')]), m(TagJoinButton, {
    model: tag
  })]), counts.length ? m('div', {
    className: 'VouchaTagActions-counts'
  }, counts.join(' · ')) : null]);
}

// ---------- Community highlights ----------

function ago(iso) {
  if (!iso) return '';
  const days = Math.floor((Date.now() - new Date(iso).getTime()) / 86400000);
  if (days < 1) return 'today';
  if (days < 7) return days + 'd';
  if (days < 60) return Math.floor(days / 7) + 'w';
  if (Math.floor(days / 30) < 12) return Math.floor(days / 30) + 'mo'; // up to 11 months, then years
  return Math.max(1, Math.floor(days / 365)) + 'y';
}
export class TagHighlights {
  view(vnode) {
    const d = tagAbout(vnode.attrs.slug);
    if (!d || !d.highlights || !d.highlights.length) return null;
    const closed = highlightsClosed();
    const toggle = () => {
      store('voucha-highlights-closed', closed ? null : '1');
    };
    return m('section', {
      className: 'VouchaHighlights' + (closed ? ' is-closed' : ''),
      'aria-label': 'Community highlights'
    }, [m('button', {
      type: 'button',
      className: 'VouchaHighlights-head',
      'aria-expanded': closed ? 'false' : 'true',
      onclick: toggle
    }, [m('i', {
      className: 'fas fa-thumbtack',
      'aria-hidden': 'true'
    }), m('span', 'Community highlights'), m('i', {
      className: 'VouchaHighlights-chevron fas fa-chevron-' + (closed ? 'down' : 'up'),
      'aria-hidden': 'true'
    })]),
    // The feed below leaves these out. Flarum doesn't redraw feed rows that haven't changed, so a class
    // added once the highlights arrive would never reach rows already drawn; a style rule reaches them all
    // (rows carry data-voucha-id, index.js).
    closed ? null : m('style', d.highlights.map(h => String(parseInt(h.id, 10))).filter(id => id !== 'NaN').map(id => `.DiscussionList [data-voucha-id="${id}"], .DiscussionList li:has(> [data-voucha-id="${id}"]) { display: none !important; }`).join('\n')), closed ? null : m('div', {
      className: 'VouchaHighlights-row',
      'data-count': Math.min(d.highlights.length, 3)
    }, d.highlights.map(h => {
      const href = app.route('discussion', {
        id: h.id + (h.slug ? '-' + h.slug : '')
      });
      const meta = [h.replies ? h.replies + (h.replies === 1 ? ' reply' : ' replies') : 'No replies yet', ago(h.createdAt)].filter(Boolean).join(' · ');
      // Reddit-style: the post's picture as a thumbnail on the left (when it has one), text beside it
      return m('a', Object.assign({
        key: h.id,
        className: 'VouchaHighlight' + (h.image ? ' has-image' : '')
      }, linkAttrs(href)), [h.image ? m('img', {
        className: 'VouchaHighlight-thumb',
        src: h.image,
        alt: '',
        loading: 'lazy',
        onerror: e => e.target.remove()
      }) : null, m('span', {
        className: 'VouchaHighlight-body'
      }, [m('span', {
        className: 'VouchaHighlight-label'
      }, [m('i', {
        className: 'fas fa-thumbtack',
        'aria-hidden': 'true'
      }), ' ', h.label || 'Pinned']), m('span', {
        className: 'VouchaHighlight-title'
      }, h.title), m('span', {
        className: 'VouchaHighlight-meta'
      }, meta)])]);
    }))]);
  }
}

// ---------- About card ----------

function factRow(f) {
  // A stat tile: the figure first, what it is under it. Exchange rates come as "1 GBP =" + "₦1,766.78":
  // the figure stays short and the label says "1 GBP in naira".
  const label = String(f.label || '');
  const rate = /=\s*$/.test(label);
  return m('li', m('a', Object.assign({
    className: 'VouchaAbout-stat',
    title: 'Source: Voucha Data Hub' + (f.period ? ' (' + f.period + ')' : '')
  }, linkAttrs(f.href || '/data-hub')), [m('strong', {
    className: 'VouchaAbout-statValue'
  }, f.value), m('span', {
    className: 'VouchaAbout-statLabel'
  }, rate ? label.replace(/\s*=\s*$/, '') + ' in naira' : label), f.period ? m('span', {
    className: 'VouchaAbout-period'
  }, f.period) : null]));
}
function aboutBody(d, extra) {
  // the country's location guide (Data Hub) leads the useful links; the card already names the country
  // then Events and Elections, on location tags (Elections on Politics too)
  const links = (d.guide ? [{
    label: 'Location guide',
    url: d.guide.url,
    icon: 'fas fa-globe-africa'
  }] : []).concat(d.events ? [{
    label: 'Events',
    url: '/events?place=' + encodeURIComponent(d.slug),
    icon: 'far fa-calendar'
  }] : []).concat(d.businesses ? [{
    label: 'Businesses',
    url: d.businesses,
    icon: 'fas fa-store'
  }] : []).concat(d.elections ? [{
    label: 'Elections',
    url: d.elections,
    icon: 'fas fa-vote-yea'
  }] : []).concat(d.links || []);
  return [d.about ? m('p', {
    className: 'VouchaAbout-text'
  }, d.about) : null, d.facts && d.facts.length ? m('div', {
    className: 'VouchaAbout-group'
  }, m('ul', {
    className: 'VouchaAbout-facts',
    'aria-label': 'Key figures'
  }, d.facts.map(factRow))) : null,
  // the page's own extras (Finance & Business: top NGX companies)
  extra ? m('div', {
    className: 'VouchaAbout-group'
  }, extra) : null, links.length ? m('div', {
    className: 'VouchaAbout-group'
  }, [m('h4', 'Useful links'), m('ul', {
    className: 'VouchaAbout-links'
  }, links.map(l => m('li', m('a', linkAttrs(l.url), [l.icon ? m('i', {
    className: l.icon + ' VouchaAbout-linkIcon',
    'aria-hidden': 'true'
  }) : null, m('span', l.label), m('i', {
    className: 'fas ' + (/^\//.test(l.url) ? 'fa-chevron-right' : 'fa-external-link-alt'),
    'aria-hidden': 'true'
  })]))))]) : null, d.rules && d.rules.length ? m('div', {
    className: 'VouchaAbout-group'
  }, [m('h4', 'Ground rules'), m('ol', {
    className: 'VouchaAbout-rules'
  }, d.rules.map((r, i) => r.detail ? m('li', m('details', [m('summary', [m('span', {
    className: 'VouchaAbout-ruleNo'
  }, i + 1), m('span', r.title)]), m('p', r.detail)])) : m('li', m('div', {
    className: 'VouchaAbout-rule'
  }, [m('span', {
    className: 'VouchaAbout-ruleNo'
  }, i + 1), m('span', r.title)]))))]) : null];
}

/** variant "side": the right column (desktop). variant "inline": above the feed (phones and tablets), collapsed. */
export class TagAboutCard {
  view(vnode) {
    const d = tagAbout(vnode.attrs.slug);
    const extra = d && vnode.attrs.extra ? vnode.attrs.extra() : null;
    if (!d || !hasAboutCard(d) && !extra) return null;
    if (vnode.attrs.variant === 'inline') {
      const open = !!this.open;
      return m('section', {
        className: 'VouchaAbout VouchaAbout--inline' + (open ? ' is-open' : '')
      }, [m('button', {
        type: 'button',
        className: 'VouchaAbout-toggle',
        'aria-expanded': open ? 'true' : 'false',
        onclick: () => this.open = !open
      }, [m('i', {
        className: 'fas fa-info-circle',
        'aria-hidden': 'true'
      }), m('span', 'About ' + d.name), m('span', {
        className: 'VouchaAbout-toggleHint'
      }, open ? 'Hide' : 'Key figures and links'), m('i', {
        className: 'fas fa-chevron-' + (open ? 'up' : 'down'),
        'aria-hidden': 'true'
      })]), open ? m('div', {
        className: 'VouchaAbout-body'
      }, aboutBody(d, extra)) : null]);
    }
    return m('section', {
      className: 'VouchaAbout VouchaAbout--side'
    }, [m('h3', {
      className: 'VouchaAbout-title'
    }, 'About ' + d.name), aboutBody(d, extra)]);
  }
}